import {
  getBaseUrl,
  getTenantDomain,
  isDa1AuthUniversalLoginHost,
  isUniversalLoginHost,
} from 'common/HelperFunctions'
import {
  logSocketConnection,
  logSocketDisconnection,
  logSocketError,
} from 'common/helpers/socketLogger'
import {io} from 'socket.io-client'

/*
 * The access token the handshake presents.
 *
 * The server derives the socket's identity from this and joins
 * `<tenant>:user:<id>` itself — a client can no longer ask for a user room by
 * name. The token is held in this module rather than read from the redux store
 * because this file is imported at app boot, before the persisted store has
 * rehydrated, and importing the store here would close an import cycle.
 */
let authToken: string | undefined

// Universal-login hosts deliberately do not open a socket. Captured once so
// setSocketAuthToken below can never turn a connection on where the app has
// decided there should not be one.
const socketEnabled =
  typeof window === 'undefined' ? true : !(isUniversalLoginHost() || isDa1AuthUniversalLoginHost())

const socket = io(getBaseUrl(), {
  query: {
    domain: getTenantDomain(),
  },
  // A function on purpose: socket.io-client calls it before EVERY connection
  // attempt, so a reconnect after a token refresh presents the new token
  // without a `reconnect_attempt` handler of our own.
  auth: (cb) => cb({token: authToken ?? ''}),
  transports: ['websocket'],
  autoConnect: socketEnabled,
})

/**
 * Hand the socket the current access token and, when it has changed, make the
 * live connection present it.
 *
 * The first connection is opened at import, before any token exists, so it
 * lands in no user room; reconnecting once the user is known is what promotes
 * it. Called from GlobalChatNotificationListener, which is mounted app-wide
 * for a signed-in user.
 */
export const setSocketAuthToken = (token?: string) => {
  if (authToken === token) return
  authToken = token

  if (!socketEnabled) return

  if (socket.connected) {
    socket.disconnect()
  }
  if (token) {
    socket.connect()
  }
}

socket.on('connect', () => {
  logSocketConnection(`Socket connected initially - ID: ${socket.id}`)
})

socket.on('disconnect', () => {
  logSocketDisconnection('Socket disconnected initially')
})

socket.on('connect_error', (error) => {
  console.error(`❌ Connection error: ${error.message}`)
  logSocketError('connect_error', `Initial connection error: ${error.message}`)
})

export default socket
